// File: frontend/src/pages/CVESearchPage.tsx
import React, { useState } from 'react';
import useSWR from 'swr';
import { useApi } from '../hooks/useApi';
import {
    Box, 
    Button, 
    TextField, 
    Typography, 
    Paper, 
    CircularProgress, 
    Alert,
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Link,
    Container,
    styled,
    alpha,
    Fade,
    Chip,
    Stack,
    InputAdornment,
    Tooltip,
    Collapse,
    IconButton,
    FormControlLabel,
    Switch
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import BugReportIcon from '@mui/icons-material/BugReport';
import SecurityIcon from '@mui/icons-material/Security';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArticleIcon from '@mui/icons-material/Article';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BusinessIcon from '@mui/icons-material/Business';
// Distinct icons for status
import WhatshotIcon from '@mui/icons-material/Whatshot';  // Exploited in Wild
import BuildCircleIcon from '@mui/icons-material/BuildCircle';  // Patch Available
import CodeIcon from '@mui/icons-material/Code';  // PoC Available
import SourceIcon from '@mui/icons-material/Source';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import GppBadIcon from '@mui/icons-material/GppBad';
import { cyber } from '../theme';
import { ExploitationBadgeStack, ExploitationSignals } from '../components/ExploitationBadges';

// Styled Components
const HeroSection = styled(Box)(({ theme }) => ({
    background: cyber.gradient,
    border: `1px solid ${alpha(cyber.primaryLight, 0.35)}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4),
    marginBottom: theme.spacing(4),
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '-50%',
        right: '-10%',
        width: '300px',
        height: '300px',
        background: 'rgba(255, 255, 255, 0.08)',
        borderRadius: '50%',
        filter: 'blur(60px)',
    }
}));

const SearchCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    background: alpha(theme.palette.background.paper, 0.9),
    backdropFilter: 'blur(10px)',
    borderRadius: theme.spacing(2),
    border: `1px solid ${alpha(cyber.primary, 0.2)}`,
    marginBottom: theme.spacing(4),
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: theme.spacing(2),
    border: `1px solid ${alpha(cyber.primary, 0.2)}`,
    '& .MuiTableHead-root': {
        background: alpha(cyber.primary, 0.06),
    },
    '& .MuiTableRow-root': {
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            background: alpha(cyber.primary, 0.05),
        }
    }
}));

const SearchButton = styled(Button)(({ theme }) => ({
    background: cyber.gradient,
    color: 'white',
    fontWeight: 'bold',
    height: '56px',
    padding: '0 32px',
    borderRadius: theme.spacing(1),
    textTransform: 'none',
    fontSize: '1rem',
    boxShadow: `0 4px 12px ${alpha(cyber.primary, 0.3)}`,
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        background: cyber.gradientHover,
        boxShadow: cyber.glow,
        transform: 'translateY(-2px)',
    },
    '&:disabled': {
        background: theme.palette.action.disabledBackground,
        color: theme.palette.action.disabled,
    }
}));

const ExampleChip = styled(Chip)(({ theme }) => ({
    cursor: 'pointer',
    fontFamily: cyber.fontMono,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: cyber.glow,
    }
}));

// Severity colors mapping
const SEVERITY_COLORS: { [key: string]: string } = {
    'CRITICAL': '#d32f2f',
    'HIGH': '#f57c00',
    'MEDIUM': '#fbc02d',
    'LOW': '#388e3c',
    'NONE': '#9e9e9e',
    'UNKNOWN': '#757575'
};

// Interfaces
interface CVEArticle {
    article_id: string;
    article_title: string;
    article_source: string;
    article_url?: string;
    mention_date: string;
    context: {
        exploited_in_wild: boolean;
        patch_available: boolean;
        poc_available: boolean;
        associated_threat_actors: string[];
        associated_malware: string[];
        context_summary: string;
    };
}

interface CVESearchResult extends ExploitationSignals {
    cve_id: string;
    description: string | null;
    cvss_v3_score: number | null;
    cvss_v3_severity: string | null;
    cvss_v3_vector: string | null;
    cwe_id: string | null;
    cwe_name: string | null;
    published_date: string | null;
    affected_products: { vendor: string; product: string; versions?: string }[];
    articles: CVEArticle[];
    article_count: number;
}

interface PatchPriorityItem extends ExploitationSignals {
    cve_id: string;
    description: string | null;
    cvss_v3_score: number | null;
    cvss_v3_severity: string | null;
    total_mentions: number;
    exploited_in_wild: boolean;
    associated_threat_actors: string[];
    associated_malware: string[];
    last_seen_date: string | null;
}

interface PatchPriorityResponse {
    items: PatchPriorityItem[];
    total: number;
    kev_only: boolean;
    generated_at: string;
}

interface CVESearchResponse {
    query: string;
    results: CVESearchResult[];
    total_count: number;
}

// Severity Chip Component
const SeverityChip: React.FC<{ severity: string | null; score: number | null }> = ({ severity, score }) => {
    const sev = severity?.toUpperCase() || 'UNKNOWN';
    const color = SEVERITY_COLORS[sev] || SEVERITY_COLORS['UNKNOWN'];
    
    return (
        <Tooltip title={score ? `CVSS Score: ${score}` : 'No CVSS score available'}>
            <Chip
                label={score ? `${sev} (${score})` : sev}
                size="small"
                sx={{
                    bgcolor: alpha(color, 0.15),
                    color: color,
                    fontWeight: 'bold',
                    border: `1px solid ${alpha(color, 0.3)}`,
                }}
            />
        </Tooltip>
    );
};

// Status Icons Component - Using distinct icons for each status
const StatusIcons: React.FC<{ 
    exploited: boolean; 
    patched: boolean; 
    poc: boolean 
}> = ({ exploited, patched, poc }) => (
    <Stack direction="row" spacing={0.5} alignItems="center">
        {/* Exploited in Wild - Fire icon */}
        <Tooltip title={exploited ? "🔥 Actively Exploited in the Wild" : "No known active exploitation"}>
            <WhatshotIcon 
                sx={{ 
                    fontSize: 20, 
                    color: exploited ? '#ff5722' : '#555',
                    opacity: exploited ? 1 : 0.4
                }} 
            />
        </Tooltip>
        
        {/* Patch Available - Build/Wrench icon */}
        <Tooltip title={patched ? "🔧 Patch Available" : "No patch available yet"}>
            <BuildCircleIcon 
                sx={{ 
                    fontSize: 20, 
                    color: patched ? cyber.accent.green : '#555',
                    opacity: patched ? 1 : 0.4
                }} 
            />
        </Tooltip>
        
        {/* PoC Available - Code icon */}
        <Tooltip title={poc ? "💻 Proof of Concept Available" : "No public PoC available"}>
            <CodeIcon 
                sx={{ 
                    fontSize: 20, 
                    color: poc ? cyber.accent.amber : '#555',
                    opacity: poc ? 1 : 0.4
                }} 
            />
        </Tooltip>
    </Stack>
);

// Expandable CVE Row Component
const CVEResultRow: React.FC<{ result: CVESearchResult }> = ({ result }) => {
    const [expanded, setExpanded] = useState(false);
    
    // Aggregate context from all articles
    const hasExploitedInWild = result.articles?.some(a => a.context?.exploited_in_wild) || false;
    const hasPatchAvailable = result.articles?.some(a => a.context?.patch_available) || false;
    const hasPoCAvailable = result.articles?.some(a => a.context?.poc_available) || false;
    
    // Calculate actual article count
    const actualArticleCount = result.articles?.length || result.article_count || 0;
    
    return (
        <>
            <TableRow>
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                        <Link
                            href={`https://nvd.nist.gov/vuln/detail/${result.cve_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                fontWeight: 'bold',
                                color: alpha(cyber.accent.cyan, 0.9),
                                textDecorationColor: alpha(cyber.accent.cyan, 0.4),
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                fontFamily: cyber.fontMono,
                                fontSize: '0.85rem'
                            }}
                        >
                            {result.cve_id}
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </Link>
                    </Box>
                </TableCell>
                <TableCell>
                    <SeverityChip severity={result.cvss_v3_severity} score={result.cvss_v3_score} />
                </TableCell>
                <TableCell>
                    <ExploitationBadgeStack signals={result} />
                    {!result.in_cisa_kev && (result.epss_score === null || result.epss_score === undefined) && (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                </TableCell>
                <TableCell>
                    <StatusIcons
                        exploited={hasExploitedInWild}
                        patched={hasPatchAvailable}
                        poc={hasPoCAvailable}
                    />
                </TableCell>
                <TableCell>
                    {result.affected_products?.slice(0, 2).map((prod, idx) => (
                        <Chip
                            key={idx}
                            icon={<BusinessIcon sx={{ fontSize: 14 }} />}
                            label={`${prod.vendor}/${prod.product}`}
                            size="small"
                            variant="outlined"
                            sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }}
                        />
                    ))}
                    {result.affected_products?.length > 2 && (
                        <Chip
                            label={`+${result.affected_products.length - 2}`}
                            size="small"
                            sx={{ fontSize: '0.7rem' }}
                        />
                    )}
                </TableCell>
                <TableCell>
                    <Chip
                        icon={<ArticleIcon sx={{ fontSize: 14 }} />}
                        label={`${actualArticleCount} article${actualArticleCount !== 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                        sx={{
                            color: cyber.accent.cyan,
                            borderColor: alpha(cyber.accent.cyan, 0.4),
                            bgcolor: alpha(cyber.accent.cyan, 0.08),
                            '& .MuiChip-icon': { color: cyber.accent.cyan },
                        }}
                    />
                </TableCell>
            </TableRow>
            
            {/* Expanded Details */}
            <TableRow>
                <TableCell colSpan={6} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 2 }}>
                            {/* Description */}
                            {result.description && (
                                <Paper sx={{ p: 2, mb: 2, bgcolor: alpha(cyber.primary, 0.05), border: `1px solid ${alpha(cyber.primary, 0.15)}` }}>
                                    <Typography variant="subtitle2" sx={{ color: cyber.primaryLight }} gutterBottom>
                                        Description
                                    </Typography>
                                    <Typography variant="body2">
                                        {result.description}
                                    </Typography>
                                </Paper>
                            )}
                            
                            {/* Technical Details */}
                            <Stack direction="row" spacing={3} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                                {result.cwe_id && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">CWE</Typography>
                                        <Typography variant="body2">
                                            <Link
                                                href={`https://cwe.mitre.org/data/definitions/${result.cwe_id.replace('CWE-', '')}.html`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ fontFamily: cyber.fontMono, fontSize: '0.8rem', color: alpha(cyber.accent.cyan, 0.9), textDecorationColor: alpha(cyber.accent.cyan, 0.4) }}
                                            >
                                                {result.cwe_id}
                                            </Link>
                                        </Typography>
                                    </Box>
                                )}
                                {result.cvss_v3_vector && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">CVSS Vector</Typography>
                                        <Typography variant="body2" sx={{ fontFamily: cyber.fontMono, fontSize: '0.75rem' }}>
                                            {result.cvss_v3_vector}
                                        </Typography>
                                    </Box>
                                )}
                                {result.published_date && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Published</Typography>
                                        <Typography variant="body2">
                                            {new Date(result.published_date).toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                            
                            {/* Status Legend */}
                            <Paper sx={{ p: 1.5, mb: 2, bgcolor: alpha(cyber.primary, 0.04) }}>
                                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <WhatshotIcon sx={{ fontSize: 18, color: hasExploitedInWild ? '#ff5722' : '#555' }} />
                                        <Typography variant="caption" color={hasExploitedInWild ? 'error' : 'text.secondary'}>
                                            {hasExploitedInWild ? 'Actively Exploited' : 'No Active Exploitation'}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <BuildCircleIcon sx={{ fontSize: 18, color: hasPatchAvailable ? cyber.accent.green : '#555' }} />
                                        <Typography variant="caption" color={hasPatchAvailable ? 'success.main' : 'text.secondary'}>
                                            {hasPatchAvailable ? 'Patch Available' : 'No Patch Yet'}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <CodeIcon sx={{ fontSize: 18, color: hasPoCAvailable ? cyber.accent.amber : '#555' }} />
                                        <Typography variant="caption" color={hasPoCAvailable ? 'warning.main' : 'text.secondary'}>
                                            {hasPoCAvailable ? 'PoC Available' : 'No Public PoC'}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Paper>
                            
                            {/* Articles mentioning this CVE */}
                            {result.articles && result.articles.length > 0 && (
                                <>
                                    <Typography variant="subtitle2" sx={{ mt: 2, color: cyber.primaryLight }} gutterBottom>
                                        Articles Mentioning This CVE ({result.articles.length})
                                    </Typography>
                                    <Stack spacing={1}>
                                        {result.articles.map((article, idx) => (
                                            <Paper 
                                                key={idx} 
                                                sx={{ 
                                                    p: 1.5, 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'space-between',
                                                    bgcolor: alpha(cyber.primary, 0.04)
                                                }}
                                            >
                                                <Box sx={{ flex: 1 }}>
                                                    {/* Article Title */}
                                                    <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                                                        {article.article_title || 'Untitled Article'}
                                                    </Typography>
                                                    
                                                    {/* Source and Date */}
                                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                                            <SourceIcon sx={{ fontSize: 14, color: alpha(cyber.accent.cyan, 0.7) }} />
                                                            <Typography variant="caption" fontWeight="medium" sx={{ color: cyber.accent.cyan, fontFamily: cyber.fontMono }}>
                                                                {article.article_source || 'Unknown Source'}
                                                            </Typography>
                                                        </Stack>
                                                        <Typography variant="caption" color="text.secondary">•</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {article.mention_date 
                                                                ? new Date(article.mention_date).toLocaleDateString('en-US', {
                                                                    year: 'numeric',
                                                                    month: 'short',
                                                                    day: 'numeric'
                                                                })
                                                                : 'Date unknown'
                                                            }
                                                        </Typography>
                                                        
                                                        {/* Status badges */}
                                                        {article.context?.exploited_in_wild && (
                                                            <Chip 
                                                                icon={<WhatshotIcon sx={{ fontSize: 12 }} />}
                                                                label="Exploited" 
                                                                size="small" 
                                                                sx={{ 
                                                                    height: 20, 
                                                                    fontSize: '0.65rem',
                                                                    bgcolor: alpha('#ff5722', 0.15),
                                                                    color: '#ff5722',
                                                                    '& .MuiChip-icon': { color: '#ff5722' }
                                                                }} 
                                                            />
                                                        )}
                                                        {article.context?.patch_available && (
                                                            <Chip 
                                                                icon={<BuildCircleIcon sx={{ fontSize: 12 }} />}
                                                                label="Patched" 
                                                                size="small" 
                                                                sx={{ 
                                                                    height: 20, 
                                                                    fontSize: '0.65rem',
                                                                    bgcolor: alpha(cyber.accent.green, 0.15),
                                                                    color: cyber.accent.green,
                                                                    '& .MuiChip-icon': { color: cyber.accent.green }
                                                                }} 
                                                            />
                                                        )}
                                                    </Stack>
                                                    
                                                    {/* Context Summary */}
                                                    {article.context?.context_summary && (
                                                        <Typography 
                                                            variant="caption" 
                                                            color="text.secondary" 
                                                            sx={{ 
                                                                display: 'block', 
                                                                fontStyle: 'italic',
                                                                mt: 0.5
                                                            }}
                                                        >
                                                            "{article.context.context_summary.length > 200 
                                                                ? article.context.context_summary.substring(0, 200) + '...'
                                                                : article.context.context_summary
                                                            }"
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Button
                                                    component={RouterLink}
                                                    to={`/report/${article.article_id}`}
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<VisibilityIcon />}
                                                    sx={{ ml: 2, textTransform: 'none', flexShrink: 0 }}
                                                >
                                                    View
                                                </Button>
                                            </Paper>
                                        ))}
                                    </Stack>
                                </>
                            )}
                            
                            {/* No articles message */}
                            {(!result.articles || result.articles.length === 0) && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    No articles found mentioning this CVE in our database.
                                </Alert>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

// Patch Priority Section — CISA KEV CVEs from the corpus ranked by EPSS.
// Answers the SOC question "what do I patch first?".
const PatchPrioritySection: React.FC = () => {
    const { authenticatedFetcher } = useApi();
    const { data, error, isLoading } = useSWR<PatchPriorityResponse>(
        '/api/cves/patch-priority?limit=15',
        authenticatedFetcher,
        { revalidateOnFocus: false }
    );

    if (error) return null; // Silent: the section is a bonus panel, not the page's core

    return (
        <Fade in timeout={800}>
            <Paper
                sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 2,
                    background: alpha('#d32f2f', 0.03),
                    border: `1px solid ${alpha('#d32f2f', 0.25)}`
                }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                    <GppBadIcon sx={{ color: '#d32f2f', fontSize: 28 }} />
                    <Typography variant="h5" fontWeight="bold">
                        Patch Priority
                    </Typography>
                    {data && (
                        <Chip
                            label={`${data.total} KEV CVEs in our corpus`}
                            size="small"
                            sx={{ bgcolor: alpha('#d32f2f', 0.15), color: '#d32f2f', fontWeight: 'bold' }}
                        />
                    )}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    CVEs from our threat intelligence corpus that are listed in the CISA Known Exploited
                    Vulnerabilities catalog, ranked by EPSS exploitation probability.
                </Typography>

                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={32} />
                    </Box>
                )}

                {data && data.items.length === 0 && (
                    <Alert severity="success" sx={{ borderRadius: 2 }}>
                        No CVEs from our corpus are currently in the CISA KEV catalog.
                    </Alert>
                )}

                {data && data.items.length > 0 && (
                    <StyledTableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', width: 40 }}>#</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>CVE ID</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Severity</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Exploitation</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        <Tooltip title="Composite 0-100 score: CVSS + EPSS + KEV + reported exploitation">
                                            <span>Risk</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Due Date</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Threat Actors</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Articles</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.items.map((item, idx) => (
                                    <TableRow key={item.cve_id}>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily: cyber.fontMono, color: 'text.secondary' }}>
                                                {idx + 1}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Link
                                                href={`https://nvd.nist.gov/vuln/detail/${item.cve_id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ fontWeight: 'bold', fontFamily: cyber.fontMono, fontSize: '0.8rem', color: alpha(cyber.accent.cyan, 0.9) }}
                                            >
                                                {item.cve_id}
                                            </Link>
                                        </TableCell>
                                        <TableCell>
                                            <SeverityChip severity={item.cvss_v3_severity} score={item.cvss_v3_score} />
                                        </TableCell>
                                        <TableCell>
                                            <ExploitationBadgeStack signals={item} />
                                        </TableCell>
                                        <TableCell>
                                            {item.risk_score !== null && item.risk_score !== undefined && (
                                                <Typography variant="body2" sx={{ fontFamily: cyber.fontMono, fontWeight: 'bold', color: item.risk_score >= 85 ? '#d32f2f' : cyber.accent.amber }}>
                                                    {item.risk_score.toFixed(1)}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" sx={{ fontFamily: cyber.fontMono }}>
                                                {item.kev_due_date || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {item.associated_threat_actors.slice(0, 3).map((actor, i) => (
                                                <Chip
                                                    key={i}
                                                    component={RouterLink}
                                                    to={`/entities?q=${encodeURIComponent(actor)}`}
                                                    clickable
                                                    label={actor}
                                                    size="small"
                                                    color="error"
                                                    variant="outlined"
                                                    sx={{ mr: 0.5, mb: 0.5, fontSize: '0.65rem', height: 20 }}
                                                />
                                            ))}
                                            {item.associated_threat_actors.length > 3 && (
                                                <Chip label={`+${item.associated_threat_actors.length - 3}`} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={<ArticleIcon sx={{ fontSize: 12 }} />}
                                                label={item.total_mentions}
                                                size="small"
                                                variant="outlined"
                                                sx={{ color: cyber.accent.cyan, borderColor: alpha(cyber.accent.cyan, 0.4), height: 20, fontSize: '0.7rem', '& .MuiChip-icon': { color: cyber.accent.cyan } }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </StyledTableContainer>
                )}
            </Paper>
        </Fade>
    );
};

// Main Component
const CVESearchPage: React.FC = () => {
    const { authenticatedFetcher } = useApi();
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState<CVESearchResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searched, setSearched] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [kevOnly, setKevOnly] = useState(false);

    const displayedResults = kevOnly ? results.filter(r => r.in_cisa_kev) : results;

    const handleSearch = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!searchTerm.trim()) return;

        setIsLoading(true);
        setError(null);
        setSearched(true);
        setResults([]);

        try {
            // Check if searching by CVE ID pattern or keyword
            const isCVEId = /^CVE-\d{4}-\d+$/i.test(searchTerm.trim());
            
            let data: CVESearchResponse;
            if (isCVEId) {
                // Direct CVE lookup
                const singleResult = await authenticatedFetcher(`/api/cves/search/${encodeURIComponent(searchTerm.trim().toUpperCase())}`);
                data = {
                    query: searchTerm,
                    results: singleResult ? [singleResult] : [],
                    total_count: singleResult ? 1 : 0
                };
            } else {
                // Keyword search
                data = await authenticatedFetcher(`/api/cves/search?q=${encodeURIComponent(searchTerm.trim())}&limit=50`);
            }
            
            setResults(data.results || []);
            setTotalCount(data.total_count || data.results?.length || 0);
        } catch (err: any) {
            if (err.message?.includes('404')) {
                setResults([]);
                setTotalCount(0);
            } else {
                setError(err.message || 'Failed to fetch search results.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const exampleSearches = [
        { label: 'CVE-2024-3400', icon: '🐛' },
        { label: 'CVE-2021-44228', icon: '🔥' },
        { label: 'Microsoft', icon: '🏢' },
        { label: 'Apache', icon: '🪶' },
        { label: 'remote code execution', icon: '💻' },
    ];

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
            <Container maxWidth="lg">
                {/* Hero Section */}
                <Fade in timeout={500}>
                    <HeroSection>
                        <Box sx={{ position: 'relative', zIndex: 1 }}>
                            <Button
                                component="a"
                                href="https://docs.ti-mindmap-hub.com/outputs/cve-intelligence"
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                                startIcon={<MenuBookIcon />}
                                endIcon={<OpenInNewIcon sx={{ fontSize: '0.75rem !important' }} />}
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    bgcolor: 'rgba(255,255,255,0.95)',
                                    color: cyber.primary,
                                    borderRadius: '20px',
                                    fontWeight: 700,
                                    fontSize: '0.78rem',
                                    px: 2,
                                    textDecoration: 'none',
                                    '&:hover': {
                                        bgcolor: 'white',
                                        boxShadow: cyber.glow,
                                    },
                                }}
                            >
                                Documentation
                            </Button>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                <BugReportIcon sx={{ fontSize: 48 }} />
                                <Typography variant="h3" component="h1" fontWeight="bold">
                                    CVE Search
                                </Typography>
                            </Stack>
                            <Typography variant="h6" sx={{ mb: 2, opacity: 0.95, maxWidth: '800px' }}>
                                Search for Common Vulnerabilities and Exposures across our threat intelligence database
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip
                                    icon={<SecurityIcon />}
                                    label="CVE IDs"
                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 'bold' }}
                                />
                                <Chip
                                    icon={<BusinessIcon />}
                                    label="Vendors"
                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 'bold' }}
                                />
                                <Chip
                                    icon={<WarningAmberIcon />}
                                    label="Exploited in Wild"
                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 'bold' }}
                                />
                            </Stack>
                        </Box>
                    </HeroSection>
                </Fade>

                {/* Search Card */}
                <Fade in timeout={700}>
                    <SearchCard elevation={3}>
                        <Box component="form" onSubmit={handleSearch} noValidate>
                            <Stack spacing={3}>
                                <Box>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <SearchIcon color="primary" />
                                        Search for CVE
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Enter a CVE ID (e.g., CVE-2024-3400) or search by keyword (vendor, product, description)
                                    </Typography>
                                </Box>

                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                    <TextField
                                        fullWidth
                                        id="cve"
                                        label="CVE ID or Keyword"
                                        name="cve"
                                        autoComplete="off"
                                        autoFocus
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="e.g., CVE-2024-3400, Microsoft, remote code execution..."
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <BugReportIcon color="primary" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                            }
                                        }}
                                    />
                                    <SearchButton
                                        type="submit"
                                        disabled={isLoading || !searchTerm.trim()}
                                        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                                    >
                                        {isLoading ? 'Searching...' : 'Search'}
                                    </SearchButton>
                                </Stack>

                                {/* Example Searches */}
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                        Example searches:
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {exampleSearches.map((example, index) => (
                                            <ExampleChip
                                                key={index}
                                                label={`${example.icon} ${example.label}`}
                                                size="small"
                                                variant="outlined"
                                                onClick={() => setSearchTerm(example.label)}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            </Stack>
                        </Box>
                    </SearchCard>
                </Fade>

                {/* Patch Priority (shown until a search is performed) */}
                {!searched && <PatchPrioritySection />}

                {/* Error Alert */}
                {error && (
                    <Fade in timeout={300}>
                        <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
                            {error}
                        </Alert>
                    </Fade>
                )}

                {/* Results Section */}
                {searched && !isLoading && !error && (
                    <Fade in timeout={900}>
                        <Box>
                            <Paper 
                                sx={{ 
                                    p: 3, 
                                    mb: 3, 
                                    borderRadius: 2,
                                    background: alpha(cyber.primary, 0.05),
                                    border: `1px solid ${alpha(cyber.primary, 0.2)}`
                                }}
                            >
                                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                                            Search Results
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Found <Box component="strong" sx={{ color: cyber.accent.cyan, fontFamily: cyber.fontMono }}>{totalCount}</Box> {totalCount === 1 ? 'CVE' : 'CVEs'} for "<strong>{searchTerm}</strong>"
                                        </Typography>
                                    </Box>
                                    {results.length > 0 && (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        size="small"
                                                        checked={kevOnly}
                                                        onChange={(e) => setKevOnly(e.target.checked)}
                                                        color="error"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: kevOnly ? '#d32f2f' : 'text.secondary' }}>
                                                        Only CISA KEV
                                                    </Typography>
                                                }
                                                sx={{ mr: 1 }}
                                            />
                                            {results.some(r => r.in_cisa_kev) && (
                                                <Chip
                                                    icon={<GppBadIcon sx={{ fontSize: 14 }} />}
                                                    label={`${results.filter(r => r.in_cisa_kev).length} in KEV`}
                                                    sx={{
                                                        bgcolor: alpha('#d32f2f', 0.15),
                                                        color: '#d32f2f',
                                                        fontWeight: 'bold',
                                                        '& .MuiChip-icon': { color: '#d32f2f' }
                                                    }}
                                                />
                                            )}
                                            {results.some(r => r.cvss_v3_severity?.toUpperCase() === 'CRITICAL') && (
                                                <Chip 
                                                    label="Contains Critical" 
                                                    sx={{ 
                                                        bgcolor: alpha('#d32f2f', 0.15), 
                                                        color: '#d32f2f',
                                                        fontWeight: 'bold'
                                                    }}
                                                />
                                            )}
                                            <Chip
                                                label={`${totalCount} CVE${totalCount !== 1 ? 's' : ''}`}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    fontFamily: cyber.fontMono,
                                                    color: cyber.accent.cyan,
                                                    bgcolor: alpha(cyber.accent.cyan, 0.12),
                                                    border: `1px solid ${alpha(cyber.accent.cyan, 0.4)}`,
                                                }}
                                            />
                                        </Stack>
                                    )}
                                </Stack>
                            </Paper>

                            {results.length > 0 ? (
                                <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                    <StyledTableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>CVE ID</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Severity</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                                        <Tooltip title="CISA KEV listing and EPSS probability of exploitation">
                                                            <span>Exploitation</span>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                                        <Tooltip title="🔥 Exploited | 🔧 Patched | 💻 PoC">
                                                            <span>Status</span>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Affected Products</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Articles</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {displayedResults.map((result) => (
                                                    <CVEResultRow key={result.cve_id} result={result} />
                                                ))}
                                                {displayedResults.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={6}>
                                                            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                                                None of the results are in the CISA KEV catalog.
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </StyledTableContainer>
                                </Paper>
                            ) : (
                                <Alert 
                                    severity="info" 
                                    sx={{ 
                                        borderRadius: 2,
                                        '& .MuiAlert-message': {
                                            width: '100%'
                                        }
                                    }}
                                >
                                    <Stack spacing={1}>
                                        <Typography variant="body1" fontWeight="bold">
                                            No CVEs found
                                        </Typography>
                                        <Typography variant="body2">
                                            The search term "<strong>{searchTerm}</strong>" did not match any CVEs in our database. 
                                            Try searching with a different CVE ID or keyword.
                                        </Typography>
                                    </Stack>
                                </Alert>
                            )}
                        </Box>
                    </Fade>
                )}
            </Container>
        </Box>
    );
};

export default CVESearchPage;